import React, { useEffect, useRef } from 'react';
import { RealtimeEvent } from '../hooks/useAnalyticsWebSocket';
import { Activity, UserPlus, LogIn, CreditCard, Layout, Zap, Globe } from 'lucide-react';

interface EventTickerProps {
    events: RealtimeEvent[];
}

export const EventTicker: React.FC<EventTickerProps> = ({ events }) => {
    const listRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to top when new events arrive
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [events]);

    const getEventIcon = (type: string) => {
        if (type.includes('signup')) return <UserPlus size={14} className="text-emerald-500" />;
        if (type.includes('login')) return <LogIn size={14} className="text-blue-500" />;
        if (type.includes('payment')) return <CreditCard size={14} className="text-purple-500" />;
        if (type.includes('board')) return <Layout size={14} className="text-amber-500" />;
        if (type.includes('channel')) return <Zap size={14} className="text-cyan-500" />;
        return <Activity size={14} className="text-neutral-500" />;
    };

    return (
        <div className="flex flex-col h-full bg-white border border-neutral-100 rounded-2xl overflow-hidden shadow-sm">
            {/* Header */}
            <div className="p-4 border-b border-neutral-50 flex items-center justify-between bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                    <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-900">Live Feed</h3>
                </div>
                <div className="text-[10px] font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full border border-blue-100 uppercase tracking-tighter">
                    Realtime
                </div>
            </div>

            {/* Event List */}
            <div
                ref={listRef}
                className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {events.length > 0 ? (
                    events.map((event, i) => (
                        <div
                            key={event.id}
                            className={`
                                px-4 py-3 flex items-start gap-3 transition-all duration-500
                                border-l-4 ${i === 0 ? 'border-blue-500 bg-blue-50/30' : 'border-transparent hover:bg-neutral-50'}
                                relative
                            `}
                        >
                            <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border transition-colors ${i === 0 ? 'bg-white border-blue-100 shadow-sm' : 'bg-neutral-50 border-neutral-100'}`}>
                                {getEventIcon(event.type)}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                    <span className={`text-[11px] font-bold truncate tracking-tight uppercase ${i === 0 ? 'text-blue-700' : 'text-neutral-700'}`}>
                                        {event.type.split('.').join(' / ')}
                                    </span>
                                    <span className="text-[10px] font-medium text-neutral-400 tabular-nums">
                                        {new Date(event.timestamp).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-[11px] text-neutral-500 truncate">
                                        {event.userId ? `User ID: ${event.userId.slice(0, 12)}...` : 'Anonymous Guest'}
                                    </span>
                                    {event.location && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <Globe size={10} className="text-neutral-300" />
                                            <span className="text-[10px] font-bold text-neutral-400 uppercase">
                                                {event.location.city || 'Remote'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="h-full flex flex-col items-center justify-center p-12 text-neutral-300">
                        <Zap size={48} className="mb-4 animate-pulse opacity-20" />
                        <span className="text-xs font-bold uppercase tracking-widest opacity-50">Listening for signals...</span>
                    </div>
                )}
            </div>

            {/* Footer / Status */}
            <div className="p-3 border-t border-neutral-50 bg-neutral-50/50 flex items-center justify-between">
                <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                    Showing {events.length} latest signals
                </span>
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]" />
                    <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Live</span>
                </div>
            </div>
        </div>
    );
};
